import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import s from "./AppShell.module.css";
import { suiteForPath, type Suite } from "./suites";
import { useNavLayout } from "./NavLayoutContext";
import { t } from "../i18n";

/* Shown only when the person asked for it: the screens should not open on a paragraph. */
const SHOWN_KEY = "sky.screenIntro.shown";

function readHidden(): boolean {
  try {
    return localStorage.getItem(SHOWN_KEY) !== "1";
  } catch {
    return true;
  }
}

function writeHidden(hidden: boolean) {
  try {
    if (hidden) localStorage.removeItem(SHOWN_KEY);
    else localStorage.setItem(SHOWN_KEY, "1");
  } catch {
    /* storage unavailable: the choice lasts for this page only */
  }
}

/**
 * One line above every screen: which suite it belongs to and what it does.
 *
 * The screen names in the sidebar are short on purpose — "What if", "Closure
 * cost" — and mean nothing to somebody seeing the platform for the first time.
 * This says what each one is for, and "About this suite" says what the group
 * is for and lists its screens. It starts hidden behind "About this screen"
 * (a client found screens that open on a paragraph crowded), and stays open
 * for whoever opens it.
 */
export function ScreenIntro() {
  const { pathname } = useLocation();
  const [hidden, setHidden] = useState(readHidden);
  const [open, setOpen] = useState(false);
  const { layout } = useNavLayout();
  const found = suiteForPath(pathname, layout);
  if (!found) return null;
  const { suite, screen } = found;

  if (hidden) {
    return (
      <div className={s.introHidden}>
        <button
          type="button"
          className={s.introLink}
          onClick={() => {
            writeHidden(false);
            setHidden(false);
          }}
        >
          {t("About this screen")}
        </button>
      </div>
    );
  }

  return (
    <section className={s.intro} aria-label={t("About this screen")}>
      <div className={s.introLine}>
        <span className={s.introSuite}>{t(suite.head)}</span>
        <span className={s.introText}>
          <b>{t(screen.label)}</b> — {t(screen.what)}
        </span>
        <span className={s.introActions}>
          <button type="button" className={s.introLink} aria-expanded={open} onClick={() => setOpen((v) => !v)}>
            {open ? t("Less") : t("About this suite")}
          </button>
          <button
            type="button"
            className={s.introLink}
            title={t("Hide these descriptions")}
            onClick={() => {
              writeHidden(true);
              setHidden(true);
            }}
          >
            {t("Hide")}
          </button>
        </span>
      </div>
      {open && <SuiteDetail suite={suite} current={screen.to} />}
    </section>
  );
}

function SuiteDetail({ suite, current }: { suite: Suite; current: string }) {
  return (
    <div className={s.introDetail}>
      {suite.summary.length > 0 ? (
        suite.summary.map((p) => <p key={p}>{t(p)}</p>)
      ) : (
        <p className={s.introOwn}>{t("A suite you arranged. Its screens:")}</p>
      )}
      <ul className={s.introScreens}>
        {suite.screens.map((sc) => (
          <li key={sc.to} aria-current={sc.to === current ? "page" : undefined}>
            <NavLink to={sc.to} end={sc.to === "/"}>
              {t(sc.label)}
            </NavLink>{" "}
            — {t(sc.what)}
          </li>
        ))}
      </ul>
    </div>
  );
}
